import { Fragment, useEffect, useRef } from 'react';
import { IoChevronDownOutline, IoChevronUpOutline } from 'react-icons/io5';
import { selectionOnPage } from './rowSelection';
import './AdminDataTable.css';

export const AdminPill = ({ children, tone = 'neutral' }) => (
  <span className={`admin-pill is-${tone}`}>{children}</span>
);

const SelectBox = ({ checked, indeterminate = false, label, onChange, disabled }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={label}
      onChange={onChange}
    />
  );
};

const SortHeader = ({ column, sort }) => {
  if (!sort || !column.sortKey) return column.header;
  const active = sort.key === column.sortKey;
  const nextOrder = active && sort.order === 'ASC' ? 'descending' : 'ascending';
  return (
    <button
      type="button"
      className={`admin-sort-btn${active ? ' is-active' : ''}`}
      aria-sort={active ? (sort.order === 'ASC' ? 'ascending' : 'descending') : 'none'}
      onClick={() => sort.onChange(column.sortKey)}
    >
      {column.header}
      <span aria-hidden="true">{active ? (sort.order === 'ASC' ? '↑' : '↓') : '↕'}</span>
      <span className="admin-sr">{active ? `sorted ${sort.order === 'ASC' ? 'ascending' : 'descending'}` : `sort ${nextOrder}`}</span>
    </button>
  );
};

const AdminDataTable = ({
  title,
  headerAside = null,
  rows,
  getRowId = (row) => row.id,
  getRowLabel = (row) => row.name || row.email || row.title || 'row',
  columns,
  selection = null,
  bulkActions = [],
  renderActions = null,
  expandedId = null,
  onToggleExpand = null,
  renderExpanded = null,
  sort = null,
  pagination = null,
  loading = false,
  error = null,
  onRetry = null,
  emptyTitle = 'Nothing here',
  emptyMessage = 'Nothing matches.'
}) => {
  const ids = rows.map(getRowId);
  const pageState = selection ? selectionOnPage(selection.selected, ids) : null;
  const selectedRows = selection ? rows.filter((row) => selection.isSelected(getRowId(row))) : [];
  const colSpan = columns.length + (selection ? 1 : 0) + (onToggleExpand ? 1 : 0) + (renderActions ? 1 : 0);

  const renderCells = (row) => columns.map((column) => (
    <td key={column.key} className={column.className || ''}>
      {column.render ? column.render(row) : row[column.key]}
    </td>
  ));

  return (
    <section className={`admin-data-table${loading ? ' is-loading' : ''}`} aria-busy={loading || undefined}>
      {(title || headerAside) && (
        <div className="admin-data-head">
          {title && <h2>{title}{loading && rows.length > 0 ? <span className="admin-data-updating">Updating…</span> : null}</h2>}
          {headerAside}
        </div>
      )}

      {selection && selection.count > 0 && (
        <div className="admin-bulk-bar" role="status">
          <strong>{selection.count} selected</strong>
          <button type="button" className="admin-bulk-clear" onClick={selection.clear}>Clear</button>
          <div className="admin-bulk-actions">
            {bulkActions.map((action) => (
              <button
                key={action.label}
                type="button"
                className={action.danger ? 'is-danger' : ''}
                onClick={() => action.onClick(selectedRows)}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && rows.length > 0 && (
        <div className="admin-table-error">
          <p>{error}</p>
          {onRetry && <button type="button" onClick={onRetry}>Try again</button>}
        </div>
      )}

      {selection && rows.length > 0 && (
        <div className="admin-mobile-select">
          <SelectBox
            checked={pageState.all}
            indeterminate={pageState.some}
            label="Select all on this page"
            onChange={() => selection.togglePage(ids)}
          />
          <span>Select all on this page</span>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="admin-data-empty">
          {loading ? (
            <h3>Loading…</h3>
          ) : (
            <>
              <h3>{error ? 'Could not load this list' : emptyTitle}</h3>
              <p>{error || emptyMessage}</p>
              {error && onRetry && <button type="button" onClick={onRetry}>Try again</button>}
            </>
          )}
        </div>
      ) : (
        <div className="admin-data-scroll">
          <table className="admin-data-grid">
            <thead>
              <tr>
                {selection && (
                  <th className="admin-select-col">
                    <SelectBox
                      checked={pageState.all}
                      indeterminate={pageState.some}
                      label="Select all on this page"
                      onChange={() => selection.togglePage(ids)}
                    />
                  </th>
                )}
                {onToggleExpand && <th className="admin-expand-col"><span className="admin-sr">Details</span></th>}
                {columns.map((column) => (
                  <th key={column.key} className={column.className || ''}>
                    <SortHeader column={column} sort={sort} />
                  </th>
                ))}
                {renderActions && <th className="admin-actions-col">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const id = getRowId(row);
                const label = getRowLabel(row);
                const open = expandedId === id;
                return (
                  <Fragment key={id}>
                    <tr className={selection?.isSelected(id) ? 'is-selected' : ''}>
                      {selection && (
                        <td className="admin-select-col">
                          <SelectBox
                            checked={selection.isSelected(id)}
                            label={`Select ${label}`}
                            onChange={() => selection.toggle(id)}
                          />
                        </td>
                      )}
                      {onToggleExpand && (
                        <td className="admin-expand-col">
                          <button
                            type="button"
                            className="admin-expand-btn"
                            aria-expanded={open}
                            aria-label={`${open ? 'Hide' : 'Show'} details for ${label}`}
                            onClick={() => onToggleExpand(id)}
                          >
                            {open ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
                          </button>
                        </td>
                      )}
                      {renderCells(row)}
                      {renderActions && (
                        <td className="admin-actions-col">
                          <div className="admin-row-actions">{renderActions(row)}</div>
                        </td>
                      )}
                    </tr>
                    {open && renderExpanded && (
                      <tr className="admin-detail-row">
                        <td colSpan={colSpan}>{renderExpanded(row)}</td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          <div className="admin-data-cards">
            {rows.map((row) => {
              const id = getRowId(row);
              const label = getRowLabel(row);
              const open = expandedId === id;
              return (
                <article key={id} className={selection?.isSelected(id) ? 'is-selected' : ''}>
                  <header>
                    {selection && (
                      <SelectBox
                        checked={selection.isSelected(id)}
                        label={`Select ${label}`}
                        onChange={() => selection.toggle(id)}
                      />
                    )}
                    <strong>{columns[0]?.render ? columns[0].render(row) : row[columns[0]?.key]}</strong>
                    {onToggleExpand && (
                      <button
                        type="button"
                        className="admin-expand-btn"
                        aria-expanded={open}
                        aria-label={`${open ? 'Hide' : 'Show'} details for ${label}`}
                        onClick={() => onToggleExpand(id)}
                      >
                        {open ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
                      </button>
                    )}
                  </header>
                  <dl>
                    {columns.slice(1).map((column) => (
                      <div key={column.key}>
                        <dt>{column.header}</dt>
                        <dd>{column.render ? column.render(row) : row[column.key]}</dd>
                      </div>
                    ))}
                  </dl>
                  {renderActions && <footer className="admin-row-actions">{renderActions(row)}</footer>}
                  {open && renderExpanded && <div className="admin-card-detail">{renderExpanded(row)}</div>}
                </article>
              );
            })}
          </div>
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="admin-data-pager">
          <button type="button" onClick={() => pagination.onPageChange(pagination.page - 1)} disabled={!pagination.hasPrev}>
            Previous
          </button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <button type="button" onClick={() => pagination.onPageChange(pagination.page + 1)} disabled={!pagination.hasNext}>
            Next
          </button>
        </div>
      )}
    </section>
  );
};

export default AdminDataTable;
